import React, { useState, useEffect } from "react";
import Layout from "../../../components/layouts/layout";
import { Nav, Table, Dropdown, Button, Card } from "react-bootstrap";
import Action from "../../../assets/images/action-vector.png";
import ConfirmOrder from "./modals/confirm-order";
import { useParams } from "react-router-dom";
import searchIcon from "../../../assets/images/search-icon.svg";
import CreateStoreRequest from "./modals/create-store-request";
import ViewStoreRequest from "./modals/view-store-request";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { RootState } from "store/store";
import {
	getAllInventoryAsync,
	searchInventoryAsync,
	getAllStoreRequestsAsync,
	getAllUsagesAsync,
	getConfirmedInventoryAsync,
	searchConfirmedInventoryAsync,
	searchStoreRequestsAsync,
	searchUsagesAsync,
} from "slices/inventory/inventorySlice";
import ViewOrder from "./modals/view-order";
import { format } from "date-fns";
import { hasPermission } from "../../../util/permissionchecker";
import { extractPermissionNames } from "../../../util/transformPermissions";
import ViewUsage from "./modals/view-usage";
import ReturnUsage from "./modals/return-usage";
import Pagination from "@mui/material/Pagination";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";

function Inventory() {
	const dispatch = useAppDispatch();
	const userData = useAppSelector((state: any) => state.user?.userData?.user);
	const isAdmin = userData?.system_role === "admin";
	const permissions = isAdmin
		? null
		: extractPermissionNames(userData?.permissions?.permissions || []);

	const [activeTab, setActiveTab] = useState<
		"confirm-order" | "material-list" | "store-request" | "usage"
	>("confirm-order");

	const handleTabSelect = (eventKey: string | null) => {
		if (eventKey) {
			setActiveTab(
				eventKey as
					| "confirm-order"
					| "material-list"
					| "store-request"
					| "usage",
			);
		}
	};

	const inventory = useAppSelector(
		(state: RootState) => state.inventory.inventory?.data,
	);
	const inventoryMeta = useAppSelector(
		(state: RootState) => state.inventory.inventory?.meta,
	);
	const confirmedInventory = useAppSelector(
		(state: RootState) => state.inventory.confirmedInventory?.data,
	);
	const confirmedInventoryMeta = useAppSelector(
		(state: RootState) => state.inventory.confirmedInventory?.meta,
	);
	const storeRequests = useAppSelector(
		(state: RootState) => state.inventory.storeRequests?.data,
	);
	const storeRequestsMeta = useAppSelector(
		(state: RootState) => state.inventory.storeRequests?.meta,
	);
	const usages = useAppSelector(
		(state: RootState) => state.inventory.usages?.data,
	);
	const usagesMeta = useAppSelector(
		(state: RootState) => state.inventory.usages?.meta,
	);

	const companyId = localStorage.getItem("userCompanyID");
	const { projectId } = useParams<{ projectId: string | undefined }>();

	const [searchQuery, setSearchQuery] = useState<string>("");
	const [searchMaterialListQuery, setSearchMaterialListQuery] =
		useState<string>("");
	const [searchStoreRequestQuery, setSearchStoreRequestQuery] =
		useState<string>("");
	const [searchUsagesQuery, setSearchUsagesQuery] = useState<string>("");
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [refreshData, setRefreshData] = useState(false);
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [pageNumber, setPage] = useState<number>(1);
	const [confirmPage, setConfirmPage] = useState(1);
	const [materialPage, setMaterialPage] = useState(1);
	const [storePage, setStorePage] = useState(1);
	const [usagePage, setUsagePage] = useState(1);

	const [pageNumbers, setPageNumbers] = useState({
		"confirm-order": 1,
		"material-list": 1,
		"store-request": 1,
		usage: 1,
	});
	useEffect(() => {
		setPageNumbers((prevState) => ({
			...prevState,
			[activeTab]: 1,
		}));
	}, [activeTab]);
	useEffect(() => {
		if (activeTab === "confirm-order") {
			setConfirmPage(1);
		} else if (activeTab === "material-list") {
			setMaterialPage(1);
		} else if (activeTab === "store-request") {
			setStorePage(1);
		} else if (activeTab === "usage") {
			setUsagePage(1);
		}
	}, [activeTab]);
	const [selectedInventoryItemId, setSelectedInventoryItemId] = useState<
		string | null
	>(null);
	const [selectedStoreRequestId, setSelectedStoreRequestId] = useState<
		string | null
	>(null);
	const [selectedUsageId, setSelectedUsageId] = useState<string | null>(null);

	const [inventoryDropdownOpen, setInventoryDropdownOpen] = useState<{
		[key: number]: boolean;
	}>({});
	const toggleInventoryDropdown = (index: number) => {
		setInventoryDropdownOpen((prevState) => ({
			...prevState,
			[index]: !prevState[index],
		}));
	};

	const [storeRequestDropdownOpen, setStoreRequestDropdownOpen] = useState<{
		[key: number]: boolean;
	}>({});
	const toggleStoreRequestDropdown = (index: number) => {
		setStoreRequestDropdownOpen((prevState) => ({
			...prevState,
			[index]: !prevState[index],
		}));
	};

	const [usageDropdownOpen, setUsageDropdownOpen] = useState<{
		[key: number]: boolean;
	}>({});
	const toggleUsageDropdown = (index: number) => {
		setUsageDropdownOpen((prevState) => ({
			...prevState,
			[index]: !prevState[index],
		}));
	};

	const [createStoreRequestModalOpen, setCreateStoreRequestModalOpen] =
		useState<boolean>(false);
	const handleOpenCreateStoreRequestModal = () =>
		setCreateStoreRequestModalOpen(true);
	const handleCloseCreateStoreRequestModal = () =>
		setCreateStoreRequestModalOpen(false);

	const [confirmOrderDropdownOpen, setConfirmOrderDropdownOpen] =
		useState<boolean>(false);
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const toggleConfirmOrderDropdown = () =>
		setConfirmOrderDropdownOpen(!confirmOrderDropdownOpen);

	const [confirmOrderModalOpen, setConfirmOrderModalOpen] =
		useState<boolean>(false);
	const handleOpenConfirmOrderModal = (inventoryItemId: string) => {
		setSelectedInventoryItemId(inventoryItemId);
		setConfirmOrderModalOpen(true);
	};
	const handleCloseConfirmOrderModal = () => {
		setConfirmOrderModalOpen(false);
		setSelectedInventoryItemId(null);
	};

	const [viewOrderModalOpen, setViewOrderModalOpen] = useState<boolean>(false);
	const handleOpenViewOrderModal = (inventoryItemId: string) => {
		setSelectedInventoryItemId(inventoryItemId);
		setViewOrderModalOpen(true);
	};
	const handleCloseViewOrderModal = () => {
		setViewOrderModalOpen(false);
		setSelectedInventoryItemId(null);
	};

	const [viewStoreRequestModalOpen, setViewStoreRequestModalOpen] =
		useState<boolean>(false);
	const handleOpenViewStoreRequestModal = (
		storeRequestItemId: string | null,
	) => {
		setSelectedStoreRequestId(storeRequestItemId);
		setViewStoreRequestModalOpen(true);
	};
	const handleCloseViewStoreRequestModal = () => {
		setViewStoreRequestModalOpen(false);
		setSelectedStoreRequestId(null);
	};

	const [viewUsageModalOpen, setViewUsageModalOpen] = useState<boolean>(false);
	const handleOpenViewUsageModal = (usageItemId: string | null) => {
		setSelectedUsageId(usageItemId);
		setViewUsageModalOpen(true);
	};
	const handleCloseViewUsageModal = () => {
		setViewUsageModalOpen(false);
		setSelectedUsageId(null);
	};

	const [returnUsageModalOpen, setReturnUsageModalOpen] =
		useState<boolean>(false);
	const handleOpenReturnUsageModal = (usageItemId: string | null) => {
		setSelectedUsageId(usageItemId);
		setReturnUsageModalOpen(true);
	};
	const handleCloseReturnUsageModal = () => {
		setReturnUsageModalOpen(false);
		setSelectedUsageId(null);
	};

	const handleInventoryChange = (
		event: React.ChangeEvent<unknown>,
		newPage: number,
	) => {
		setPageNumbers((prevState) => ({
			...prevState,
			"confirm-order": newPage,
		}));
		setConfirmPage(newPage);
		if (companyId && projectId) {
			if (searchQuery.trim() === "") {
				dispatch(
					getAllInventoryAsync({
						company_id: companyId,
						projectId: projectId,
						page: newPage,
					}),
				);
			} else {
				dispatch(
					searchInventoryAsync({
						company_id: companyId,
						projectId: projectId,
						searchTerm: searchQuery,
						page: newPage,
					}),
				);
			}
		}
	};
	const handleMaterialListChange = (
		event: React.ChangeEvent<unknown>,
		newPage: number,
	) => {
		setPageNumbers((prevState) => ({
			...prevState,
			"material-list": newPage,
		}));
		setMaterialPage(newPage);
		if (companyId && projectId) {
			if (searchMaterialListQuery.trim() === "") {
				dispatch(
					getConfirmedInventoryAsync({
						company_id: companyId,
						projectId: projectId,
						page: newPage,
					}),
				);
			} else {
				dispatch(
					searchConfirmedInventoryAsync({
						company_id: companyId,
						projectId: projectId,
						searchTerm: searchQuery,
						page: newPage,
					}),
				);
			}
		}
	};
	const handleStoreRequestsChange = (
		event: React.ChangeEvent<unknown>,
		newPage: number,
	) => {
		setPageNumbers((prevState) => ({
			...prevState,
			"store-request": newPage,
		}));
		setStorePage(newPage);
		if (companyId && projectId) {
			if (searchStoreRequestQuery.trim() === "") {
				dispatch(
					getAllStoreRequestsAsync({
						company_id: companyId,
						projectId: projectId,
						page: newPage,
					}),
				);
			} else {
				dispatch(
					searchStoreRequestsAsync({
						company_id: companyId,
						projectId: projectId,
						searchTerm: searchQuery,
						page: newPage,
					}),
				);
			}
		}
	};
	const handleUsagesChange = (
		event: React.ChangeEvent<unknown>,
		newPage: number,
	) => {
		setPageNumbers((prevState) => ({
			...prevState,
			usage: newPage,
		}));
		setUsagePage(newPage);
		if (companyId && projectId) {
			if (searchUsagesQuery.trim() === "") {
				dispatch(
					getAllUsagesAsync({
						company_id: companyId,
						projectId: projectId,
						page: newPage,
					}),
				);
			} else {
				dispatch(
					searchUsagesAsync({
						company_id: companyId,
						projectId: projectId,
						searchTerm: searchQuery,
						page: newPage,
					}),
				);
			}
		}
	};

	useEffect(() => {
		if (companyId && projectId && !searchQuery) {
			const currentPage = pageNumbers[activeTab];

			if (activeTab === "confirm-order") {
				dispatch(
					getAllInventoryAsync({
						company_id: companyId,
						projectId: projectId,
						page: currentPage,
					}),
				);
			}
		}
	}, [companyId, dispatch, pageNumbers, projectId, searchQuery, activeTab]);
	useEffect(() => {
		if (companyId && projectId && searchMaterialListQuery === "") {
			const currentPage = pageNumbers[activeTab];

			if (activeTab === "material-list") {
				dispatch(
					getConfirmedInventoryAsync({
						company_id: companyId,
						projectId: projectId,
						page: currentPage,
					}),
				);
			}
		}
	}, [
		companyId,
		dispatch,
		pageNumbers,
		projectId,
		searchMaterialListQuery,
		activeTab,
	]);
	useEffect(() => {
		if (companyId && projectId && searchStoreRequestQuery === "") {
			const currentPage = pageNumbers[activeTab];

			if (activeTab === "store-request") {
				dispatch(
					getAllStoreRequestsAsync({
						company_id: companyId,
						projectId: projectId,
						page: currentPage,
					}),
				);
			}
		}
	}, [
		companyId,
		dispatch,
		pageNumbers,
		projectId,
		searchStoreRequestQuery,
		activeTab,
	]);
	useEffect(() => {
		if (companyId && projectId && searchUsagesQuery === "") {
			const currentPage = pageNumbers[activeTab];

			if (activeTab === "usage") {
				dispatch(
					getAllUsagesAsync({
						company_id: companyId,
						projectId: projectId,
						page: currentPage,
					}),
				);
			}
		}
	}, [
		companyId,
		dispatch,
		pageNumbers,
		projectId,
		searchUsagesQuery,
		activeTab,
	]);

	const handleCreateSuccess = () => {
		setRefreshData(true);
	};

	const getStatusColor = (status: any) => {
		switch (status.toLowerCase()) {
			case "confirmed":
				return "green";
			case "pending":
				return "goldenrod";
			case "incomplete":
				return "red";
			default:
				return "gray";
		}
	};
	const getConfirmedStatusColor = (status: any) => {
		switch (status.toLowerCase()) {
			case "available":
				return "green";
			case "low":
				return "goldenrod";
			default:
				return "red";
		}
	};

	return (
		<div className="bg-color-8">
			<Layout>
				<div className="mt-5 p-5">
					<h3>Inventory</h3>
					<Nav
						activeKey={activeTab}
						onSelect={handleTabSelect}
						className="nav px-2 py-2"
					>
						<Nav.Item>
							<Nav.Link
								className="py-0 px-2 rounded fw-light mx-2"
								eventKey="confirm-order"
								style={{
									color:
										activeTab === "confirm-order" ? " #FFFFFF  " : " #747487",
									textDecoration: "none",
									backgroundColor:
										activeTab === "confirm-order" ? "#0F6CBD " : "transparent",
								}}
							>
								Confirm order
							</Nav.Link>
						</Nav.Item>
						<Nav.Item className="">
							<Nav.Link
								className="py-0 px-2 rounded fw-light mx-2"
								eventKey="material-list"
								style={{
									color:
										activeTab === "material-list" ? " #FFFFFF  " : " #747487",
									textDecoration: "none",
									backgroundColor:
										activeTab === "material-list" ? "#0F6CBD " : "transparent",
								}}
							>
								Material list
							</Nav.Link>
						</Nav.Item>
						<Nav.Item>
							<Nav.Link
								className="py-0 px-2 rounded fw-light mx-2"
								eventKey="store-request"
								style={{
									color:
										activeTab === "store-request" ? " #FFFFFF  " : " #747487",
									textDecoration: "none",
									backgroundColor:
										activeTab === "store-request" ? "#0F6CBD " : "transparent",
								}}
							>
								Store request
							</Nav.Link>
						</Nav.Item>
						<Nav.Item>
							<Nav.Link
								className="py-0 px-2 rounded fw-light mx-2"
								eventKey="usage"
								style={{
									color: activeTab === "usage" ? " #FFFFFF  " : " #747487",
									textDecoration: "none",
									backgroundColor:
										activeTab === "usage" ? "#0F6CBD " : "transparent",
								}}
							>
								Usage
							</Nav.Link>
						</Nav.Item>
					</Nav>
					<div>
						{activeTab === "confirm-order" && (
							<>
								<div className="d-inline-flex justify-content-end w-100 mt-4">
									<Formik
										initialValues={{ searchQuery: "" }}
										validationSchema={Yup.object({
											searchQuery: Yup.string(),
										})}
										onSubmit={async (values, { setSubmitting, setErrors }) => {
											try {
												setSubmitting(true);

												setSearchQuery(values.searchQuery);
												if (
													values.searchQuery.trim() === "" &&
													companyId &&
													projectId
												) {
													await dispatch(
														getAllInventoryAsync({
															company_id: companyId,
															projectId: projectId,
															page: 1,
														}),
													);
												} else {
													await dispatch(
														searchInventoryAsync({
															company_id: companyId,
															projectId: projectId,
															searchTerm: values.searchQuery,
															page: 1,
														}),
													).unwrap();
												}
											} catch (error) {
												setErrors({
													searchQuery: "An error occurred. Please try again.",
												});
											} finally {
												setSubmitting(false);
											}
										}}
									>
										{({ isSubmitting, handleSubmit, errors, touched }) => (
											<Form onSubmit={handleSubmit} className="mt-4">
												<div className="mx-2 border border-1 rounded py-1 px-2 d-inline-flex justify-content-between align-items-center my-3">
													<Field
														type="search"
														name="searchQuery"
														placeholder="Search for material"
														className="bg-transparent border-0"
														style={{ outline: "0" }}
													/>
													<button
														type="submit"
														className={`pointer ${
															isSubmitting ? "disabled" : ""
														}`}
														disabled={isSubmitting}
														style={{
															background: "none",
															border: "none",
															boxShadow: "none",
														}}
														aria-label="search"
													>
														<img src={searchIcon} alt="search icon" />
													</button>
												</div>
												{errors.searchQuery && touched.searchQuery ? (
													<div className="text-danger">
														{errors.searchQuery}
													</div>
												) : (
													<div className="text-transparent">
														errors.searchQuery
													</div>
												)}
											</Form>
										)}
									</Formik>
								</div>
								<div className="bg-color-9 table-container px-5 pt-3">
									<Table borderless responsive>
										<thead>
											<tr>
												<th className="text-color-1 bg-color-3">S/N</th>
												<th className="text-color-1 bg-color-3">Date </th>
												<th className="text-color-1 bg-color-3">Material </th>
												<th className="text-color-1 bg-color-3">
													Qty ordered{" "}
												</th>
												<th className="text-color-1 bg-color-3">
													Qty received{" "}
												</th>
												<th className="text-color-1 bg-color-3">
													Qty remaining
												</th>
												<th className="text-color-1 bg-color-3">Status</th>
												<th className="text-color-1 bg-color-3">Action</th>
											</tr>
										</thead>
										<tbody>
											{inventory?.map((item, index) => (
												<tr key={item.id}>
													<td style={{ verticalAlign: "middle" }}>
														{inventory.indexOf(item) + 1}
													</td>
													<td style={{ verticalAlign: "middle" }}>
														{format(new Date(item.created_at), "dd/MM/yy")}
													</td>
													<td style={{ verticalAlign: "middle" }}>
														{item.material}
													</td>
													<td style={{ verticalAlign: "middle" }}>
														{item.quantity_ordered}
													</td>
													<td style={{ verticalAlign: "middle" }}>
														{item.confirmed_quantity}
													</td>
													<td style={{ verticalAlign: "middle" }}>
														{item.quantity_ordered - item.confirmed_quantity}
													</td>
													<td
														style={{
															color: getStatusColor(item.status),
															verticalAlign: "middle",
														}}
													>
														{item.status}
													</td>
													<td style={{ verticalAlign: "middle" }}>
														<Dropdown
															show={inventoryDropdownOpen[index]}
															onToggle={() => toggleInventoryDropdown(index)}
														>
															<Dropdown.Toggle
																className="dropdown-toggle"
																variant="transparent"
																style={{
																	background: "none",
																	border: "none",
																	boxShadow: "none",
																}}
															>
																<img
																	src={Action}
																	alt="Action"
																	className="action-icon"
																	style={{ margin: "0", padding: "0" }}
																/>
															</Dropdown.Toggle>
															<Dropdown.Menu>
																<Dropdown.Item
																	onClick={() =>
																		handleOpenViewOrderModal(item.id)
																	}
																>
																	View
																</Dropdown.Item>
																{hasPermission(
																	permissions,
																	"confirm-inventory",
																) && (
																	<Dropdown.Item
																		onClick={() =>
																			handleOpenConfirmOrderModal(item.id)
																		}
																	>
																		Confirm
																	</Dropdown.Item>
																)}
															</Dropdown.Menu>
														</Dropdown>
													</td>
												</tr>
											))}
										</tbody>
									</Table>
									{inventoryMeta && inventoryMeta.total_pages > 1 && (
										<div className="d-flex justify-content-center mt-3 pb-5">
											<Pagination
												count={inventoryMeta && inventoryMeta?.total_pages}
												page={confirmPage}
												onChange={handleInventoryChange}
												shape="rounded"
												color="primary"
											/>
										</div>
									)}
								</div>
							</>
						)}
						{activeTab === "material-list" && (
							<div className="">
								<div className="mt-2 d-inline-flex justify-content-between w-100">
									<div className="d-inline-flex justify-content-between w-">
										<Card className="bg-color-5 text-color-4 border-0 p-2 mx-1 w-fit">
											<small>Material count</small>
											<h3>{confirmedInventoryMeta?.total_materials}</h3>
										</Card>

										<Card className="bg-color-6 text-color-5 border-0 p-2 mx-1 w-fit">
											<small>Total instock </small>
											<h3>
												{confirmedInventoryMeta?.total_available_quantity}
											</h3>
										</Card>

										<Card className="bg-color-7 text-color-6 border-0 p-2 mx-1 w-fit">
											<small> Total out of stock</small>
											<h3>{confirmedInventoryMeta?.total_out_of_stock}</h3>
										</Card>
									</div>
									<div className="d-inline-flex justify-content-end w-100">
										<Formik
											initialValues={{ searchMaterialListQuery: "" }}
											validationSchema={Yup.object({
												searchMaterialListQuery: Yup.string(),
											})}
											onSubmit={async (
												values,
												{ setSubmitting, setErrors },
											) => {
												try {
													setSubmitting(true);

													setSearchMaterialListQuery(
														values.searchMaterialListQuery,
													);
													if (
														values.searchMaterialListQuery.trim() === "" &&
														companyId &&
														projectId
													) {
														await dispatch(
															getConfirmedInventoryAsync({
																company_id: companyId,
																projectId: projectId,
																page: 1,
															}),
														);
													} else {
														await dispatch(
															searchConfirmedInventoryAsync({
																company_id: companyId,
																projectId: projectId,
																searchTerm: values.searchMaterialListQuery,
																page: 1,
															}),
														).unwrap();
													}
												} catch (error) {
													setErrors({
														searchMaterialListQuery:
															"An error occurred. Please try again.",
													});
												} finally {
													setSubmitting(false);
												}
											}}
										>
											{({ isSubmitting, handleSubmit, errors, touched }) => (
												<Form onSubmit={handleSubmit} className="mt-4">
													<div className="mx-2 border border-1 rounded py-1 px-2 d-inline-flex justify-content-between align-items-center my-3">
														<Field
															type="search"
															name="searchMaterialListQuery"
															placeholder="Search for material"
															className="bg-transparent border-0"
															style={{ outline: "0" }}
														/>
														<button
															type="submit"
															className={`pointer ${
																isSubmitting ? "disabled" : ""
															}`}
															disabled={isSubmitting}
															style={{
																background: "none",
																border: "none",
																boxShadow: "none",
															}}
															aria-label="search"
														>
															<img src={searchIcon} alt="search icon" />
														</button>
													</div>
													{errors.searchMaterialListQuery &&
													touched.searchMaterialListQuery ? (
														<div className="text-danger">
															{errors.searchMaterialListQuery}
														</div>
													) : (
														<div className="text-transparent">
															errors.searchMaterialListQuery
														</div>
													)}
												</Form>
											)}
										</Formik>
									</div>
								</div>
								<div className="bg-color-9 table-container px-5 mt-4">
									<Table borderless responsive>
										<thead>
											<tr>
												<th className="text-color-1 bg-color-3">S/N</th>
												<th className="text-color-1 bg-color-3">
													Material name{" "}
												</th>
												<th className="text-color-1 bg-color-3">Unit</th>
												<th className="text-color-1 bg-color-3">Instock</th>
												<th className="text-color-1 bg-color-3">Status</th>
											</tr>
										</thead>
										<tbody>
											{confirmedInventory?.map((item, index) => (
												<tr key={item.id}>
													<td style={{ verticalAlign: "middle" }}>
														{confirmedInventory.indexOf(item) + 1}
													</td>
													<td style={{ verticalAlign: "middle" }}>
														{item.material}
													</td>
													<td style={{ verticalAlign: "middle" }}>
														{item.unit}
													</td>
													<td style={{ verticalAlign: "middle" }}>
														{item.Instock}
													</td>
													<td
														style={{
															color: getConfirmedStatusColor(item.Status),
															verticalAlign: "middle",
														}}
													>
														{item.Status}
													</td>
												</tr>
											))}
										</tbody>
									</Table>
									{confirmedInventoryMeta &&
										confirmedInventoryMeta.total_pages > 1 && (
											<div className="d-flex justify-content-center mt-3 pb-5">
												<Pagination
													count={
														confirmedInventoryMeta &&
														confirmedInventoryMeta?.total_pages
													}
													page={materialPage}
													onChange={handleMaterialListChange}
													shape="rounded"
													color="primary"
												/>
											</div>
										)}
								</div>
							</div>
						)}
						{activeTab === "store-request" && (
							<>
								<div className="w-100 d-inline-flex justify-content-end mt-4">
									{hasPermission(permissions, "create-inventory") && (
										<Button
											onClick={handleOpenCreateStoreRequestModal}
											className="border-0 bg-color-1 float-end"
										>
											<span className="text-center">Create store request</span>
										</Button>
									)}
									<div className="d-inline-flex justify-content-end w-100 mt-4">
										<Formik
											initialValues={{ searchStoreRequestQuery: "" }}
											validationSchema={Yup.object({
												searchStoreRequestQuery: Yup.string(),
											})}
											onSubmit={async (
												values,
												{ setSubmitting, setErrors },
											) => {
												try {
													setSubmitting(true);

													setSearchStoreRequestQuery(
														values.searchStoreRequestQuery,
													);
													if (
														values.searchStoreRequestQuery.trim() === "" &&
														companyId &&
														projectId
													) {
														await dispatch(
															getAllStoreRequestsAsync({
																company_id: companyId,
																projectId: projectId,
																page: 1,
															}),
														);
													} else {
														await dispatch(
															searchStoreRequestsAsync({
																company_id: companyId,
																projectId: projectId,
																searchTerm: values.searchStoreRequestQuery,
																page: 1,
															}),
														).unwrap();
													}
												} catch (error) {
													setErrors({
														searchStoreRequestQuery:
															"An error occurred. Please try again.",
													});
												} finally {
													setSubmitting(false);
												}
											}}
										>
											{({ isSubmitting, handleSubmit, errors, touched }) => (
												<Form onSubmit={handleSubmit} className="mt-4">
													<div className="mx-2 border border-1 rounded py-1 px-2 d-inline-flex justify-content-between align-items-center my-3">
														<Field
															type="search"
															name="searchStoreRequestQuery"
															placeholder="Search for request"
															className="bg-transparent border-0"
															style={{ outline: "0" }}
														/>
														<button
															type="submit"
															className={`pointer ${
																isSubmitting ? "disabled" : ""
															}`}
															disabled={isSubmitting}
															style={{
																background: "none",
																border: "none",
																boxShadow: "none",
															}}
															aria-label="search"
														>
															<img src={searchIcon} alt="search icon" />
														</button>
													</div>
													{errors.searchStoreRequestQuery &&
													touched.searchStoreRequestQuery ? (
														<div className="text-danger">
															{errors.searchStoreRequestQuery}
														</div>
													) : (
														<div className="text-transparent">
															errors.searchStoreRequestQuery
														</div>
													)}
												</Form>
											)}
										</Formik>
									</div>
								</div>

								<div className="bg-color-9 table-container px-5 pt-3">
									<Table borderless responsive>
										<thead>
											<tr>
												<th className="text-color-1 bg-color-3">S/N</th>
												<th className="text-color-1 bg-color-3">Date </th>
												<th className="text-color-1 bg-color-3">Material </th>
												<th className="text-color-1 bg-color-3">
													Requested by{" "}
												</th>
												<th className="text-color-1 bg-color-3">
													Collected by{" "}
												</th>
												<th className="text-color-1 bg-color-3">
													Qty collected
												</th>
												<th className="text-color-1 bg-color-3">Action</th>
											</tr>
										</thead>
										<tbody>
											{storeRequests?.map((item, index) => (
												<tr key={item.id}>
													<td style={{ verticalAlign: "middle" }}>
														{storeRequests.indexOf(item) + 1}
													</td>
													<td>
														{format(
															new Date(item.confirmed_inventory.created_at),
															"dd/MM/yy",
														)}
													</td>
													<td>{item.confirmed_inventory.material_name}</td>
													<td>{item.created_by}</td>
													<td>{item.collected_by}</td>
													<td>{item.quantity_collected}</td>
													<td style={{ padding: "0", verticalAlign: "middle" }}>
														<Dropdown
															show={storeRequestDropdownOpen[index]}
															onToggle={() => toggleStoreRequestDropdown(index)}
														>
															<Dropdown.Toggle
																className="dropdown-toggle"
																variant="transparent"
																style={{
																	background: "none",
																	border: "none",
																	boxShadow: "none",
																}}
															>
																<img
																	src={Action}
																	alt="Action"
																	className="action-icon mb-3"
																	style={{ margin: "0", padding: "0" }}
																/>
															</Dropdown.Toggle>
															<Dropdown.Menu>
																<Dropdown.Item
																	onClick={() =>
																		handleOpenViewStoreRequestModal(item.id)
																	}
																>
																	View
																</Dropdown.Item>
															</Dropdown.Menu>
														</Dropdown>
													</td>
												</tr>
											))}
										</tbody>
									</Table>
									{storeRequestsMeta && storeRequestsMeta.total_pages > 1 && (
										<div className="d-flex justify-content-center mt-3 pb-5">
											<Pagination
												count={
													storeRequestsMeta && storeRequestsMeta?.total_pages
												}
												page={storePage}
												onChange={handleStoreRequestsChange}
												shape="rounded"
												color="primary"
											/>
										</div>
									)}
								</div>
							</>
						)}
						{/* Usage tab */}
						{activeTab === "usage" && (
							<>
								<div className="d-inline-flex justify-content-end w-100 mt-4">
									<Formik
										initialValues={{ searchUsages: "" }}
										validationSchema={Yup.object({
											searchUsages: Yup.string(),
										})}
										onSubmit={async (values, { setSubmitting, setErrors }) => {
											try {
												setSubmitting(true);

												setSearchUsagesQuery(values.searchUsages);
												if (
													values.searchUsages.trim() === "" &&
													companyId &&
													projectId
												) {
													await dispatch(
														getAllUsagesAsync({
															company_id: companyId,
															projectId: projectId,
															page: 1,
														}),
													);
												} else {
													await dispatch(
														searchUsagesAsync({
															company_id: companyId,
															projectId: projectId,
															searchTerm: values.searchUsages,
															page: 1,
														}),
													).unwrap();
												}
											} catch (error) {
												setErrors({
													searchUsages: "An error occurred. Please try again.",
												});
											} finally {
												setSubmitting(false);
											}
										}}
									>
										{({ isSubmitting, handleSubmit, errors, touched }) => (
											<Form onSubmit={handleSubmit} className="mt-4">
												<div className="mx-2 border border-1 rounded py-1 px-2 d-inline-flex justify-content-between align-items-center my-3">
													<Field
														type="search"
														name="searchUsages"
														placeholder="Search for material"
														className="bg-transparent border-0"
														style={{ outline: "0" }}
													/>
													<button
														type="submit"
														className={`pointer ${
															isSubmitting ? "disabled" : ""
														}`}
														disabled={isSubmitting}
														style={{
															background: "none",
															border: "none",
															boxShadow: "none",
														}}
														aria-label="search"
													>
														<img src={searchIcon} alt="search icon" />
													</button>
												</div>
												{errors.searchUsages && touched.searchUsages ? (
													<div className="text-danger">
														{errors.searchUsages}
													</div>
												) : (
													<div className="text-transparent">
														errors.searchUsages
													</div>
												)}
											</Form>
										)}
									</Formik>
								</div>
								<div className="bg-color-9 table-container px-5 pt-3">
									<Table borderless responsive>
										<thead>
											<tr>
												<th className="text-color-1 bg-color-3">Date </th>
												<th className="text-color-1 bg-color-3">Material </th>
												<th className="text-color-1 bg-color-3">
													Qty collected{" "}
												</th>
												<th className="text-color-1 bg-color-3">
													Qty returned{" "}
												</th>
												<th className="text-color-1 bg-color-3">Qty used</th>
												<th className="text-color-1 bg-color-3">Action</th>
											</tr>
										</thead>
										<tbody>
											{usages?.map((item, index) => (
												<tr key={item.id}>
													<td style={{ verticalAlign: "middle" }}>
														{format(new Date(item.created_at), "dd/MM/yy")}
													</td>
													<td style={{ verticalAlign: "middle" }}>
														{item.material_name}
													</td>
													<td style={{ verticalAlign: "middle" }}>
														{item.quantity}
													</td>
													<td style={{ verticalAlign: "middle" }}>
														{item.quantity_returned === null
															? 0
															: item.quantity_returned}
													</td>
													<td style={{ verticalAlign: "middle" }}>
														{item.quantity_used}
													</td>
													<td style={{ verticalAlign: "middle" }}>
														<Dropdown
															show={usageDropdownOpen[index]}
															onToggle={() => toggleUsageDropdown(index)}
														>
															<Dropdown.Toggle
																className="dropdown-toggle"
																variant="transparent"
																style={{
																	background: "none",
																	border: "none",
																	boxShadow: "none",
																}}
															>
																<img
																	src={Action}
																	alt="Action"
																	className="action-icon"
																	style={{ margin: "0", padding: "0" }}
																/>
															</Dropdown.Toggle>
															<Dropdown.Menu>
																<Dropdown.Item
																	onClick={() =>
																		handleOpenViewUsageModal(item.id)
																	}
																>
																	View
																</Dropdown.Item>
																{item.quantity_returned === null ? (
																	<Dropdown.Item
																		onClick={() =>
																			handleOpenReturnUsageModal(item.id)
																		}
																	>
																		Return
																	</Dropdown.Item>
																) : (
																	<></>
																)}
															</Dropdown.Menu>
														</Dropdown>
													</td>
												</tr>
											))}
										</tbody>
									</Table>
									{usagesMeta && usagesMeta.total_pages > 1 && (
										<div className="d-flex justify-content-center mt-3 pb-5">
											<Pagination
												count={usagesMeta && usagesMeta?.total_pages}
												page={usagePage}
												onChange={handleUsagesChange}
												shape="rounded"
												color="primary"
											/>
										</div>
									)}
								</div>
							</>
						)}
					</div>
				</div>
				<ConfirmOrder
					show={confirmOrderModalOpen}
					closeModal={handleCloseConfirmOrderModal}
					inventoryItemId={selectedInventoryItemId}
					onConfirmSuccess={handleCreateSuccess}
					projectId={projectId}
				/>
				<CreateStoreRequest
					show={createStoreRequestModalOpen}
					inventory={confirmedInventory}
					closeModal={handleCloseCreateStoreRequestModal}
					onCreateSuccess={handleCreateSuccess}
				/>
				<ViewOrder
					show={viewOrderModalOpen}
					closeModal={handleCloseViewOrderModal}
					id={selectedInventoryItemId}
					project_id={projectId}
					company_id={companyId}
				/>
				<ViewStoreRequest
					closeModal={handleCloseViewStoreRequestModal}
					company_id={companyId}
					id={selectedStoreRequestId}
					project_id={projectId}
					show={viewStoreRequestModalOpen}
				/>
				<ViewUsage
					closeModal={handleCloseViewUsageModal}
					company_id={companyId}
					id={selectedUsageId}
					project_id={projectId}
					show={viewUsageModalOpen}
				/>
				<ReturnUsage
					closeModal={handleCloseReturnUsageModal}
					onReturnSuccess={handleCreateSuccess}
					projectId={projectId}
					show={returnUsageModalOpen}
					storeRequestId={selectedUsageId}
				/>
			</Layout>
		</div>
	);
}

export default Inventory;
